// components/reports/OrderReport.jsx
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Calendar,
  ShoppingBag,
  CreditCard,
  Truck,
  Package,
  Store,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { EnhancedBarChart } from '../../components/charts/EnhancedBarChart';
import { RadialProgressChart } from '../../components/charts/RadialProgressChart';

export function OrderReport({ data, orderData, onExport, onViewDetails }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderModeFilter, setOrderModeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  if (!orderData || !Array.isArray(orderData)) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12">
            <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold">No order data available</h3>
            <p className="text-muted-foreground">Please check your data source</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Process order data
  const processedData = useMemo(() => {
    const paidOrders = orderData.filter(order => order.OrderStatus === 'Paid');
    const confirmedOrders = orderData.filter(order => order.OrderStatus === 'Confirmed');
    const cancelledOrders = orderData.filter(order => order.OrderStatus === 'Cancel');

    // Order status distribution
    const statusData = [
      { name: 'Paid', value: paidOrders.length, percentage: Math.round((paidOrders.length / orderData.length) * 100) },
      { name: 'Confirmed', value: confirmedOrders.length, percentage: Math.round((confirmedOrders.length / orderData.length) * 100) },
      { name: 'Cancelled', value: cancelledOrders.length, percentage: Math.round((cancelledOrders.length / orderData.length) * 100) }
    ];

    // Order mode distribution
    const modeData = {};
    orderData.forEach(order => {
      const mode = order.OrderMode;
      if (!modeData[mode]) modeData[mode] = 0;
      modeData[mode]++;
    });

    // Hourly order distribution
    const hourlyData = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      orders: 0
    }));
    orderData.forEach(order => {
      const hour = new Date(order.OrderDateTime).getHours();
      hourlyData[hour].orders++;
    });

    // Day-wise orders
    const dayData = {};
    orderData.forEach(order => {
      const day = new Date(order.OrderDateTime).toLocaleDateString('en-US', { weekday: 'short' });
      if (!dayData[day]) dayData[day] = 0;
      dayData[day]++;
    });

    // Payment method distribution
    const paymentData = {};
    orderData.forEach(order => {
      const payment = order.PaymentMode || 'Unknown';
      if (!paymentData[payment]) paymentData[payment] = 0;
      paymentData[payment]++;
    });

    // Source distribution
    const sourceData = {};
    orderData.forEach(order => {
      const source = order.OrderSource || 'Unknown';
      if (!sourceData[source]) sourceData[source] = 0;
      sourceData[source]++;
    });

    // Calculate metrics
    const totalOrders = orderData.length;
    const successfulOrders = paidOrders.length;
    const cancellationRate = Math.round((cancelledOrders.length / orderData.length) * 100);
    const avgOrderValue = paidOrders.length > 0 
      ? paidOrders.reduce((sum, order) => sum + (order.NetAmount || 0), 0) / paidOrders.length 
      : 0;

    // Growth calculations (mock data for demo)
    const yesterdayOrders = Math.round(totalOrders * 0.8);
    const orderGrowth = Math.round(((totalOrders - yesterdayOrders) / yesterdayOrders) * 100);

    return {
      statusData,
      modeData: Object.entries(modeData).map(([mode, count]) => ({ name: mode, value: count })),
      hourlyData,
      dayData: Object.entries(dayData).map(([day, count]) => ({ name: day, value: count })),
      paymentData: Object.entries(paymentData).map(([payment, count]) => ({ name: payment, value: count })),
      sourceData: Object.entries(sourceData).map(([source, count]) => ({ name: source, value: count })),
      metrics: {
        totalOrders,
        successfulOrders,
        cancellationRate,
        avgOrderValue,
        orderGrowth,
        totalRevenue: paidOrders.reduce((sum, order) => sum + (order.NetAmount || 0), 0),
        confirmedRevenue: confirmedOrders.reduce((sum, order) => sum + (order.NetAmount || 0), 0),
        cancelledRevenue: cancelledOrders.reduce((sum, order) => sum + (order.NetAmount || 0), 0)
      }
    };
  }, [orderData]);

  // Filter and sort order data for table
  const filteredOrders = useMemo(() => {
    let result = [...orderData];

    // Apply search
    if (searchTerm) {
      result = result.filter(order => 
        order.OrderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.BranchName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.PaymentMode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.OrderMode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (statusFilter !== 'all') {
      result = result.filter(order => order.OrderStatus === statusFilter);
    }
    if (orderModeFilter !== 'all') {
      result = result.filter(order => order.OrderMode === orderModeFilter);
    }
    if (paymentFilter !== 'all') {
      result = result.filter(order => order.PaymentMode === paymentFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result.slice(0, 10); // Limit to 10 for preview
  }, [orderData, searchTerm, statusFilter, orderModeFilter, paymentFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExport = () => {
    if (onExport) onExport('orders');
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Paid':
        return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle className="h-3 w-3 mr-1" /> Paid</Badge>;
      case 'Confirmed':
        return <Badge className="bg-blue-500 hover:bg-blue-600"><Clock className="h-3 w-3 mr-1" /> Confirmed</Badge>;
      case 'Cancel':
        return <Badge className="bg-red-500 hover:bg-red-600"><XCircle className="h-3 w-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getModeIcon = (mode) => {
    switch(mode) {
      case 'Delivery':
        return <Truck className="h-4 w-4 mr-2" />;
      case 'Pickup':
        return <Package className="h-4 w-4 mr-2" />;
      case 'DineIn':
        return <Store className="h-4 w-4 mr-2" />;
      default:
        return <ShoppingBag className="h-4 w-4 mr-2" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-bold">{processedData.metrics.totalOrders}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ShoppingBag className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              {processedData.metrics.orderGrowth >= 0 ? (
                <>
                  <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-500">+{processedData.metrics.orderGrowth}%</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                  <span className="text-sm text-red-500">{processedData.metrics.orderGrowth}%</span>
                </>
              )}
              <span className="text-sm text-muted-foreground ml-2">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Successful Orders</p>
                <p className="text-2xl font-bold">{processedData.metrics.successfulOrders}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <span className="text-sm text-muted-foreground">
                {Math.round((processedData.metrics.successfulOrders / processedData.metrics.totalOrders) * 100)}% success rate
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Order Value</p>
                <p className="text-2xl font-bold">
                  RS {processedData.metrics.avgOrderValue.toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+5.3%</span>
              <span className="text-sm text-muted-foreground ml-2">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cancellation Rate</p>
                <p className="text-2xl font-bold">{processedData.metrics.cancellationRate}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-500" />
              </div>
            </div>
            <div className="flex items-center mt-2">
              <ArrowDownRight className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">-2.1%</span>
              <span className="text-sm text-muted-foreground ml-2">vs last week</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        <EnhancedBarChart
          data={processedData.dayData}
          title="Day-wise Order Distribution"
          color="#3b82f6"
          onExport={() => handleExport()}
        />
        
        <EnhancedBarChart
          data={processedData.modeData}
          title="Order Mode Distribution"
          color="#10b981"
          onExport={() => handleExport()}
        />
      </div>

      {/* Order Status and Payment Distribution */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Order Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {processedData.statusData.map((status, index) => {
                const colors = ['#10b981', '#3b82f6', '#ef4444'];
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: colors[index] }}
                        />
                        <span className="font-medium">{status.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{status.value}</p>
                        <p className="text-sm text-muted-foreground">{status.percentage}%</p>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full"
                        style={{ 
                          backgroundColor: colors[index],
                          width: `${status.percentage}%`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Method Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {processedData.paymentData.map((payment, index) => {
                const colors = ['#10b981', '#3b82f6', '#f59e0b'];
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        payment.name === 'CASH' ? 'bg-green-500/10' : 
                        payment.name === 'CARD' ? 'bg-blue-500/10' : 'bg-yellow-500/10'
                      }`}>
                        <CreditCard className={`h-4 w-4 ${
                          payment.name === 'CASH' ? 'text-green-500' : 
                          payment.name === 'CARD' ? 'text-blue-500' : 'text-yellow-500'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">{payment.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {Math.round((payment.value / processedData.metrics.totalOrders) * 100)}% of orders
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{payment.value}</p>
                      <p className="text-sm text-muted-foreground">orders</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order List Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Recent Orders
            </CardTitle>
            
            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  className="pl-9 md:w-[200px]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Confirmed">Confirmed</SelectItem>
                  <SelectItem value="Cancel">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={orderModeFilter} onValueChange={setOrderModeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modes</SelectItem>
                  <SelectItem value="Delivery">Delivery</SelectItem>
                  <SelectItem value="Pickup">Pickup</SelectItem>
                  <SelectItem value="DineIn">Dine In</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('OrderNumber')}>
                    Order #
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('OrderDateTime')}>
                    Date & Time
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('NetAmount')}>
                    Amount
                  </TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{order.OrderNumber}</TableCell>
                    <TableCell>
                      {new Date(order.OrderDateTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>{getStatusBadge(order.OrderStatus)}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {getModeIcon(order.OrderMode)}
                        {order.OrderMode}
                      </div>
                    </TableCell>
                    <TableCell className="font-bold">
                      RS {order.NetAmount ? order.NetAmount.toLocaleString() : '0'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {order.PaymentMode === 'CASH' ? (
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                        )}
                        {order.PaymentMode || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>{order.BranchName}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => onViewDetails && onViewDetails('order-details')}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredOrders.length === 0 && (
              <div className="text-center py-12">
                <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold">No orders found</h3>
                <p className="text-muted-foreground">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {filteredOrders.length} of {orderData.length} orders
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export Orders
              </Button>
              <Button onClick={() => onViewDetails && onViewDetails('orders')}>
                <Eye className="h-4 w-4 mr-2" />
                View All Orders
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Order Sources</p>
                <p className="text-2xl font-bold">
                  {processedData.sourceData.length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Store className="h-5 w-5 text-primary" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {processedData.sourceData.slice(0, 3).map((source, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span>{source.name}</span>
                  <span className="font-medium">{source.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Peak Hours</p>
                <p className="text-2xl font-bold">
                  {processedData.hourlyData.sort((a, b) => b.orders - a.orders)[0]?.hour}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {processedData.hourlyData
                .sort((a, b) => b.orders - a.orders)
                .slice(0, 3)
                .map((hour, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span>{hour.hour}</span>
                    <span className="font-medium">{hour.orders} orders</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Preparation</p>
                <p className="text-2xl font-bold">18m</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-500" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span>Fastest</span>
                <span className="font-medium">12m</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Slowest</span>
                <span className="font-medium">35m</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}